/**
 * =============================================================================
 * GLOBAL CURRENCY UTILITIES
 * =============================================================================
 * 
 * This is the SINGLE SOURCE OF TRUTH for all currency operations in the app.
 * 
 * RULES:
 * 1. ALL monetary values in the database are stored in USD
 * 2. Conversion happens ONLY at display time, never during calculations
 * 3. Use these utilities EVERYWHERE - components, AG Grid, exports, charts
 * 4. Never use raw `$` or `toLocaleString` for money - always use these helpers
 * 
 * DEBUG MODE:
 * Set localStorage.setItem('CURRENCY_DEBUG', 'true') to enable tracing
 */

// ============================================================================
// CONSTANTS
// ============================================================================

export const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$',
  CHF: 'CHF', CNY: '¥', INR: '₹', MXN: 'MX$', BRL: 'R$', KRW: '₩',
  SGD: 'S$', HKD: 'HK$', NOK: 'kr', SEK: 'kr', DKK: 'kr', NZD: 'NZ$',
  ZAR: 'R', RUB: '₽', TRY: '₺', PLN: 'zł', THB: '฿', IDR: 'Rp',
  MYR: 'RM', PHP: '₱', AED: 'د.إ', SAR: '﷼', ILS: '₪', EGP: 'E£',
  VND: '₫', CLP: 'CLP$', COP: 'COP$', PEN: 'S/', ARS: 'ARS$',
};

export const BASE_CURRENCY = 'USD';
export const STORAGE_KEY_CURRENCY = 'global_display_currency';
export const STORAGE_KEY_RATES = 'custom_currency_rates';

export const getDecimalPrecision = () => {
  try { const s = localStorage.getItem("global_decimal_precision"); return s ? parseInt(s, 10) : 3; } catch { return 3; }
};

// ============================================================================
// CORE GETTERS (read from localStorage)
// ============================================================================

/**
 * Get the current display currency code
 * @returns {string} Currency code (e.g., 'EUR', 'USD')
 */
export const getDisplayCurrency = () => {
  try {
    return localStorage.getItem(STORAGE_KEY_CURRENCY) || BASE_CURRENCY;
  } catch {
    return BASE_CURRENCY;
  }
};

/**
 * Get the symbol for the current display currency
 * @returns {string} Currency symbol (e.g., '€', '$')
 */
export const getDisplaySymbol = () => {
  const currency = getDisplayCurrency();
  return CURRENCY_SYMBOLS[currency] || currency;
};

/**
 * Get exchange rate for a currency (1 USD = X currency)
 * @param {string} currencyCode - Currency to get rate for
 * @returns {number} Exchange rate, or 1 if not found
 */
// Default fallback rates for common currencies (approximate)
const DEFAULT_FALLBACK_RATES = { EUR: 0.92, GBP: 0.79, JPY: 149.5, CAD: 1.36, AUD: 1.53 };

export const getExchangeRate = (currencyCode) => {
  const normalizedCode = (currencyCode || BASE_CURRENCY).toUpperCase();
  if (normalizedCode === BASE_CURRENCY) return 1;
  
  try {
    const ratesStr = localStorage.getItem(STORAGE_KEY_RATES);
    const rates = ratesStr ? JSON.parse(ratesStr) : {};
    const rate = rates[normalizedCode];
    // Use stored rate if available, otherwise use default fallback rate
    if (rate && rate > 0) return rate;
    return DEFAULT_FALLBACK_RATES[normalizedCode] || 1;
  } catch {
    return DEFAULT_FALLBACK_RATES[normalizedCode] || 1;
  }
};

/**
 * Get the current display currency's exchange rate
 * @returns {number} Exchange rate
 */
export const getCurrentRate = () => {
  return getExchangeRate(getDisplayCurrency());
};

/**
 * Check if currency conversion is currently enabled
 * @returns {boolean} True if displaying non-USD currency with valid rate
 */
export const isConversionEnabled = () => {
  const currency = getDisplayCurrency();
  if (currency === BASE_CURRENCY) return false;
  const rate = getExchangeRate(currency);
  return rate !== 1;
};

// ============================================================================
// CONVERSION FUNCTIONS
// ============================================================================

/**
 * Convert USD amount to display currency
 * @param {number|string} usdAmount - Amount in USD
 * @param {Object} options - Optional settings
 * @param {string} options.debugSource - Source for debug logging
 * @returns {number} Converted amount
 */
export const convertFromUSD = (usdAmount, options = {}) => {
  const numAmount = parseFloat(usdAmount) || 0;
  
  if (!isConversionEnabled()) {
    return numAmount;
  }
  
  const rate = getCurrentRate();
  const converted = numAmount * rate;
  
  // Debug logging
  if (options.debugSource && localStorage.getItem('CURRENCY_DEBUG') === 'true') {
    console.log(`[CURRENCY] ${options.debugSource}: $${numAmount} → ${getDisplaySymbol()}${converted.toFixed(3)} (rate: ${rate})`);
  }
  
  return converted;
};

/**
 * Convert display currency amount back to USD
 * @param {number|string} amount - Amount in display currency
 * @param {string} fromCurrency - Source currency (optional, uses current display currency)
 * @returns {number} Amount in USD
 */
export const convertToUSD = (amount, fromCurrency = null) => {
  const numAmount = parseFloat(amount) || 0;
  const currency = fromCurrency || getDisplayCurrency();
  
  if (currency === BASE_CURRENCY) {
    return numAmount;
  }
  
  const rate = getExchangeRate(currency);
  if (rate <= 0) return numAmount;
  
  return numAmount / rate;
};

// ============================================================================
// FORMATTING FUNCTIONS
// ============================================================================

/**
 * Format USD amount in display currency with symbol
 * This is the PRIMARY function for displaying money in the UI
 * 
 * @param {number|string} usdAmount - Amount in USD (from database)
 * @param {Object} options - Formatting options
 * @param {number} options.decimals - Decimal places (default: 3)
 * @param {boolean} options.showSymbol - Include currency symbol (default: true)
 * @param {boolean} options.showSign - Show +/- for positive/negative (default: false)
 * @param {string} options.debugSource - Source for debug logging
 * @returns {string} Formatted string like "€ 1,234.567" or "$ 1,234.567"
 */
export const formatMoney = (usdAmount, options = {}) => {
  const {
    decimals = getDecimalPrecision(),
    showSymbol = true,
    showSign = false,
    debugSource,
  } = options;
  
  const numAmount = parseFloat(usdAmount) || 0;
  const converted = convertFromUSD(numAmount, { debugSource });
  
  let formatted = Math.abs(converted).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  
  // Add sign if requested
  if (showSign) {
    if (converted > 0) formatted = '+' + formatted;
    else if (converted < 0) formatted = '-' + formatted;
  } else if (converted < 0) {
    formatted = '-' + formatted;
  }
  
  if (showSymbol) {
    const symbol = getDisplaySymbol();
    return `${symbol} ${formatted}`;
  }
  
  return formatted;
};

/**
 * Format USD amount for export (plain number in display currency)
 * @param {number|string} usdAmount - Amount in USD
 * @param {number} decimals - Decimal places
 * @returns {number} Converted number
 */
export const formatMoneyForExport = (usdAmount, decimals = getDecimalPrecision()) => {
  const converted = convertFromUSD(usdAmount);
  return parseFloat(converted.toFixed(decimals));
};

/**
 * Get formatting info for tooltip display
 * @param {number|string} usdAmount - Amount in USD
 * @param {number} decimals - Decimal places
 * @returns {Object} { displayValue, usdValue, rate, isConverted, currency, symbol }
 */
export const getMoneyTooltipInfo = (usdAmount, decimals = getDecimalPrecision()) => {
  const numAmount = parseFloat(usdAmount) || 0;
  const currency = getDisplayCurrency();
  const symbol = getDisplaySymbol();
  const rate = getCurrentRate();
  const isConverted = isConversionEnabled();
  
  const usdFormatted = numAmount.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  
  if (!isConverted) {
    return {
      displayValue: `$ ${usdFormatted}`,
      usdValue: `$ ${usdFormatted}`,
      rate: 1,
      isConverted: false,
      currency: BASE_CURRENCY,
      symbol: '$',
    };
  }
  
  const converted = numAmount * rate;
  const convertedFormatted = converted.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  
  return {
    displayValue: `${symbol} ${convertedFormatted}`,
    usdValue: `$ ${usdFormatted}`,
    rate,
    isConverted: true,
    currency,
    symbol,
  };
};

// ============================================================================
// AG GRID UTILITIES
// ============================================================================

/**
 * Create a value getter for AG Grid that converts currency for sorting/filtering
 * @param {string} field - Field name to get value from
 * @returns {Function} AG Grid valueGetter function
 */
export const currencyValueGetter = (field) => (params) => {
  const value = params.data?.[field];
  if (value == null || value === undefined) return 0;
  return convertFromUSD(value);
};

/**
 * Create a value formatter for AG Grid (simple text, no React)
 * @param {number} decimals - Decimal places
 * @returns {Function} AG Grid valueFormatter function
 */
export const currencyValueFormatter = (decimals = getDecimalPrecision()) => (params) => {
  if (params.value == null || params.value === undefined) return '-';
  return formatMoney(params.value, { decimals });
};

/**
 * Aggregation function that sums USD values then converts
 * Use this for group row aggregations
 * @param {Array} values - Array of USD values to sum
 * @returns {number} Sum converted to display currency
 */
export const currencyAggSum = (values) => {
  const usdSum = values.reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  return convertFromUSD(usdSum);
};

/**
 * Create pinned row data with converted currency values
 * @param {Array} data - Array of row data
 * @param {Array} currencyFields - Fields that contain currency values
 * @param {string} labelField - Field for the label (e.g., 'Total')
 * @param {string} label - Label text
 * @returns {Object} Pinned row data
 */
export const createCurrencyPinnedRow = (data, currencyFields, labelField, label) => {
  const pinnedRow = { [labelField]: label };
  
  currencyFields.forEach(field => {
    const usdSum = data.reduce((sum, row) => sum + (parseFloat(row[field]) || 0), 0);
    pinnedRow[field] = usdSum; // Keep as USD, conversion happens in cellRenderer
  });
  
  return pinnedRow;
};

/**
 * Create a complete currency column definition for AG Grid
 * @param {string} field - Field name
 * @param {string} headerName - Column header
 * @param {Object} options - Additional column options
 * @returns {Object} AG Grid column definition
 */
export const createCurrencyColumnDef = (field, headerName, options = {}) => {
  const { decimals = getDecimalPrecision(), ...rest } = options;
  
  return {
    headerName,
    field,
    valueGetter: currencyValueGetter(field),
    valueFormatter: currencyValueFormatter(decimals),
    cellStyle: { textAlign: 'right' },
    filter: 'agNumberColumnFilter',
    sortable: true,
    type: 'numericColumn',
    ...rest,
  };
};

// ============================================================================
// EXPORT UTILITIES
// ============================================================================

/**
 * Get export header with currency info
 * @returns {Object} { currency, symbol, rate, note }
 */
export const getExportCurrencyInfo = () => {
  const currency = getDisplayCurrency();
  const symbol = getDisplaySymbol();
  const rate = getCurrentRate();
  const isConverted = isConversionEnabled();
  
  return {
    currency,
    symbol,
    rate,
    isConverted,
    note: isConverted 
      ? `Values converted from USD (Rate: 1 USD = ${rate.toFixed(3)} ${currency})`
      : 'Values in USD',
  };
};

/**
 * Format a row's currency fields for export
 * @param {Object} row - Data row
 * @param {Array} currencyFields - Fields to convert
 * @returns {Object} Row with converted values
 */
export const formatRowForExport = (row, currencyFields) => {
  const converted = { ...row };
  currencyFields.forEach(field => {
    if (converted[field] != null) {
      converted[field] = formatMoneyForExport(converted[field]);
    }
  });
  return converted;
};

// ============================================================================
// REACT HOOK INTEGRATION
// ============================================================================

/**
 * Subscribe to currency changes (call from useEffect)
 * @param {Function} callback - Function to call when currency changes
 * @returns {Function} Cleanup function
 */
export const subscribeToCurrencyChanges = (callback) => {
  const handler = () => callback();
  
  window.addEventListener('globalCurrencyChanged', handler);
  window.addEventListener('currencyRatesChanged', handler);
  
  return () => {
    window.removeEventListener('globalCurrencyChanged', handler);
    window.removeEventListener('currencyRatesChanged', handler);
  };
};

// ============================================================================
// VALIDATION & DEBUG
// ============================================================================

/**
 * Validate that a value hasn't been double-converted
 * Call this in development to catch conversion bugs
 * @param {number} value - Value to check
 * @param {string} source - Source description for logging
 */
export const assertNotConverted = (value, source) => {
  if (process.env.NODE_ENV === 'development') {
    // This is a heuristic check - if values are wildly different from expected
    // it might indicate double conversion
    const rate = getCurrentRate();
    if (rate > 1 && Math.abs(value) > 1000000000) {
      console.warn(`[CURRENCY WARNING] Possible double conversion at ${source}: value=${value}`);
    }
  }
};

/**
 * Log currency state for debugging
 */
export const logCurrencyState = () => {
  console.group('[CURRENCY STATE]');
  console.log('Display Currency:', getDisplayCurrency());
  console.log('Symbol:', getDisplaySymbol());
  console.log('Rate:', getCurrentRate());
  console.log('Conversion Enabled:', isConversionEnabled());
  console.log('All Rates:', (() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY_RATES) || '{}');
    } catch { return {}; }
  })());
  console.groupEnd();
};

// Expose debug function globally in development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.debugCurrency = logCurrencyState;
}

